import 'simplebar-react/dist/simplebar.min.css'

import type { HandwrittenNoteTypes } from '@mathflat/handwritten-note'
import { ReadOnlyHandwrittenNote } from '@mathflat/handwritten-note'
import { s3URL } from '@mathflat/shared/@common/utils/s3'
import MathflatPlayer from '@mathflat/videoplayer'
import { clsx } from 'clsx'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { isIOS } from 'react-device-detect'
import SimpleBar from 'simplebar-react'

import { handwrittenNoteApi, HandwrittenNoteType } from '~/@common/api/handwrittenNoteApi'
import { useStudentAppMediaQuery } from '~/@common/hooks/useMediaQuery'
import modalService from '~/@common/services/modal.service'
import { monitoringService } from '~/@common/services/monitoring.service'
import { commonRepo } from '~/@common/services/repo.service'
import { colors } from '~/@common/styles'
import { Drawer } from '~/@common/ui/(Drawer)/BaseDrawer'
import { Icon } from '~/@common/ui/Icon/Icon'
import { mixpanelClient } from '~/@common/utils/trackings/mixpanel'
import ProblemNoteMobileViewer from '~/@pages/student/@widgets/ProblemNote/ProblemNoteMobileViewer'
import type { StudentWorkbookDetailService } from '~/@pages/student-workbook/detail/@service/StudentWorkbookDetail.service'

import type { ProblemScoring } from '../../../@trait/ProblemScoring.trait'
import type { ProblemScoringViewOption } from '../../../@trait/ProblemScoringViewOption.trait'
import { S } from './AnswerVideoDrawer.style'
import { isProblemVideoSectionOpen, isSolutionSectionOpen } from './AnswerVideoDrawer.utils'

type AnswerVideoDrawerProps = {
  contentId?: string
  problemScoring: ProblemScoring<'WORKSHEET' | 'WORKBOOK'>
  viewOption: ProblemScoringViewOption<'학습모듈' | 'NOT_학습모듈'>
  isSubmittedAnswerAvailable?: boolean
  openDrawer: boolean
  onSubmitVideoAssist?: StudentWorkbookDetailService['onSubmitVideoAssist']
}
export const AnswerVideoDrawer = ({
  contentId,
  problemScoring,
  viewOption,
  isSubmittedAnswerAvailable,
  openDrawer,
  onSubmitVideoAssist,
}: AnswerVideoDrawerProps) => {
  const { isMobile } = useStudentAppMediaQuery()
  const [_isMobile, setIsMobile] = useState(isMobile)
  const [isOrientLocked, setIsOrientLocked] = useState(false)
  const [isShowProblemImage, setIsShowProblemImage] = useState(false)
  const [isHiddenNoteData, setIsHiddenNoteData] = useState(false)
  const [noteData, setNoteData] = useState<HandwrittenNoteTypes.NoteData | undefined>()
  const [teacherNoteData, setTeacherNoteData] = useState<
    HandwrittenNoteTypes.NoteData | undefined
  >()
  const [videoPlaybackTime, setVideoPlaybackTime] = useState(0)
  const [isOpenNoteViewer, setIsOpenNoteViewer] = useState(false)

  const isViewerWorkbook =
    viewOption.content.type === 'CUSTOM_SIGNATURE' || viewOption.content.type === 'ACADEMY_GROUP'

  const hasNoteData = useMemo(() => noteData || teacherNoteData, [noteData, teacherNoteData])

  useEffect(() => {
    if (!openDrawer) {
      setVideoPlaybackTime(0)
      return
    }
    if (isOrientLocked) return
    setIsMobile(isMobile)
  }, [isOrientLocked, isMobile, openDrawer])

  useEffect(() => {
    if (!openDrawer || !problemScoring.handwrittenNoteUrl) return
    // 학생 본인 필기 차단
    if (viewOption.isHandwrittenRestricted) return
    handwrittenNoteApi
      .fetchNoteByUrl(problemScoring.handwrittenNoteUrl)
      .then((data) => {
        setNoteData(data)
      })
      .catch((e) => {
        console.error(e)
      })
  }, [problemScoring.handwrittenNoteUrl, openDrawer, viewOption.isHandwrittenRestricted])

  // 제출 전/후 polling으로 선생님의 필기 가져오기
  const fetchTeacherNote = useCallback(async () => {
    if (!contentId) return
    return await handwrittenNoteApi.fetchNote({
      id: +contentId,
      subId: +problemScoring.id,
      type: HandwrittenNoteType.STUDENT_WORKSHEET_SCORING_TEACHER,
    })
  }, [contentId, problemScoring.id])

  useEffect(() => {
    if (!contentId) return
    // 실시간모니터링 일때만 필기 저장 방식이 server임
    const isLiveLesson = problemScoring.handwrittenNoteSaveStorage === 'SERVER'

    if (!openDrawer || !isLiveLesson) return
    // 선생님 필기 restriction 차단
    if (viewOption.isTeacherHandwrittenRestricted) return

    const listener = monitoringService.listenMessage<Awaited<ReturnType<typeof fetchTeacherNote>>>({
      callback: fetchTeacherNote,
      onSuccess: (data) => {
        if (!data) return
        setTeacherNoteData(data)
      },
      onError: () => {
        listener.stop()
      },
    })

    if (isOpenNoteViewer || isHiddenNoteData) {
      listener.stop()
    }
    return () => {
      listener.stop()
    }
  }, [
    isHiddenNoteData,
    isOpenNoteViewer,
    problemScoring.handwrittenNoteSaveStorage,
    problemScoring.id,
    problemScoring.isSubmitted,
    openDrawer,
    contentId,
    fetchTeacherNote,
    viewOption.isTeacherHandwrittenRestricted,
  ])

  const onCloseProblemNoteMobileViewer = () => {
    setIsOpenNoteViewer(false)
    modalService.closeModal()
  }
  const openNoteViewer = () => {
    setIsOpenNoteViewer(true)
    if (hasNoteData && problemScoring.문제이미지) {
      modalService.openModal(
        <ProblemNoteMobileViewer
          noteData={noteData}
          fetchTeacherNote={fetchTeacherNote}
          problemImgUrl={problemScoring.문제이미지}
          onClose={onCloseProblemNoteMobileViewer}
        />,
        { modalName: '내풀이 확대 모달' },
      )
    }
  }

  const toggleHiddenNoteData = () => {
    setIsHiddenNoteData((prev) => !prev)
  }

  const closeDrawer = () => {
    setIsHiddenNoteData(false)
  }

  const 정답해설_공개 = isSolutionSectionOpen(viewOption, problemScoring)
  const 문풀동공개 = isProblemVideoSectionOpen(viewOption, problemScoring)

  const onPlayCallback = () => {
    if ('video' in problemScoring.problem && problemScoring.problem.video?.id) {
      const trackingInfo = (() => {
        if (viewOption.content.kind === 'WORKSHEET' && viewOption.content.type === 'CUSTOM') {
          return {
            trackKey: '[내학습지] 빠른채점_문풀동_재생',
            trackData: {
              '학제/학년': problemScoring.학제학년학기,
            },
          }
        }
        if (viewOption.content.kind === 'WORKBOOK') {
          if (viewOption.content.type === 'CUSTOM') {
            return {
              trackKey: '[내교재] 풀이_문풀동_재생',
              trackData: {
                '학년/학기': problemScoring.학제학년학기,
              },
            }
          }
          if (viewOption.content.type === 'CUSTOM_SIGNATURE') {
            return {
              trackKey: '[시그니처교재] 풀이_문풀동_재생',
              trackData: {
                '학년/학기': problemScoring.학제학년학기,
              },
            }
          }
          if (viewOption.content.type === 'ACADEMY_GROUP') {
            return {
              trackKey: '[학원그룹교재] 풀이_문풀동_재생',
              trackData: {
                '학년/학기': problemScoring.학제학년학기,
              },
            }
          }
        }
        return null
      })()

      if (trackingInfo && problemScoring.problem.video.id && problemScoring.problemId) {
        window.freshpaint?.track(trackingInfo.trackKey, {
          비디오id: problemScoring.problem?.video?.id,
          문제id: problemScoring.problemId,
          정오: problemScoring.채점결과 === 'NONE' ? null : problemScoring.채점결과,
          ...trackingInfo.trackData,
        })
      }
    }

    if (!onSubmitVideoAssist) return
    if (!problemScoring.isSubmitted && viewOption.studentAppSetting?.채점전문풀동공개) {
      onSubmitVideoAssist({
        workbookProblemId: problemScoring.contentProblemId,
        assistType: 'WATCHED_SOLVING_VIDEO_BEFORE_SCORING',
      })
    }
    if (problemScoring.isSubmitted && viewOption.studentAppSetting?.채점후문풀동공개) {
      onSubmitVideoAssist({
        workbookProblemId: problemScoring.contentProblemId,
        assistType: 'WATCHED_SOLVING_VIDEO_AFTER_SCORING',
      })
    }
  }
  const emptyCase = useMemo(() => {
    if (!isSubmittedAnswerAvailable) {
      return <></>
    }

    if (!problemScoring.handwrittenNoteUrl && _isMobile) {
      return <div className="mobile-text">필기한 기록이 없어요</div>
    }

    return (
      <SimpleBar
        key="without-note-data"
        style={{
          maxHeight: 정답해설_공개 || 문풀동공개 ? '312px' : '100%',
        }}
      >
        <div className="problem-image-container">
          <img src={problemScoring.문제이미지} alt="문제 이미지" width={344} />
        </div>
      </SimpleBar>
    )
  }, [problemScoring.handwrittenNoteUrl, isSubmittedAnswerAvailable])

  const 해설내풀이문구_상세명 = useMemo(() => {
    return (
      <>
        {/* 풀이동영상 라벨 — restriction 차단 시 미노출 */}
        {!viewOption.isVideoRestricted && (
          <span className="explation-submitted-answer-text">풀이동영상</span>
        )}
        {/* 필기 restriction 차단 시 "내풀이" 단어 제거 → "문제" 단독 표기 */}
        {problemScoring.isSubmitted && isSubmittedAnswerAvailable && (
          <span className="explation-submitted-answer-text">
            {viewOption.isHandwrittenRestricted ? '문제' : '문제 & 내풀이'}
          </span>
        )}
        {!!problemScoring.문제해설이미지 && (
          <span className="explation-submitted-answer-text">해설</span>
        )}
      </>
    )
  }, [
    problemScoring.isSubmitted,
    !viewOption.studentAppSetting,
    viewOption.studentAppSetting?.채점후정답해설공개,
    viewOption.isVideoRestricted,
    viewOption.isHandwrittenRestricted,
    problemScoring.문제해설이미지,
    isSubmittedAnswerAvailable,
  ])

  return (
    <Drawer
      size="534px"
      isOpened={openDrawer}
      placement={_isMobile ? 'bottom' : 'left'}
      backdropOpacity={_isMobile ? 1 : 0}
      closeDrawer={closeDrawer}
      {...(!_isMobile && { rootSelector: '#app', position: 'absolute' })}
    >
      <Drawer.Content css={S.problemScoringDrawerContentStyle}>
        <Drawer.Header className="problem-scoring-drawer-header">
          {해설내풀이문구_상세명}
        </Drawer.Header>
        <Drawer.Body className="problem-scoring-drawer-body">
          <S.DrawerBodyContent
            onClick={(e) => {
              e.stopPropagation()
            }}
          >
            {/* 풀이동영상 영역 — restriction 차단 시 영역째 미렌더 (학부모는 production 토큰에 video restriction 함께 내려옴) */}
            {!viewOption.isVideoRestricted && (
              <div className="problem-scoring-drawer-body-content-item">
                <div className="title">풀이동영상</div>
                {!문풀동공개 ? (
                  <div className="no-video">
                    {problemScoring.isSubmitted && '채점 후 '}
                    풀이 영상이 비공개 상태입니다.
                  </div>
                ) : (
                  <>
                    {'video' in problemScoring.problem && problemScoring.problem.video ? (
                      <MathflatPlayer
                        onFullScreenCallback={() => {
                          setIsOrientLocked(true)
                        }}
                        onChangeFullScreenState={(state: boolean) => {
                          if (!state) {
                            setIsOrientLocked(false)
                          }
                        }}
                        ThumbnailElement={
                          !isViewerWorkbook ? (
                            <S.Thumbnail>
                              <div className="img-bg">
                                {problemScoring.문제이미지 && (
                                  <img src={problemScoring.문제이미지} style={{ width: '100%' }} />
                                )}
                              </div>
                              {problemScoring.problem.videoMatched === false && (
                                <img
                                  className="thumbnail-guide"
                                  src={s3URL.common(
                                    'images/video/problem-solving/thumbnail_guide@3x.png',
                                  )}
                                  srcSet={`${s3URL.common('images/video/problem-solving/thumbnail_guide@4x.png')} 4x`}
                                />
                              )}

                              <div className="gradation">
                                <img
                                  className="sub-title"
                                  src={s3URL.common(
                                    'images/video/problem-solving/sub-title_student-app@1x.png',
                                  )}
                                  srcSet={`${s3URL.common(
                                    'images/video/problem-solving/sub-title_student-app@2x.png',
                                  )} 2x, ${s3URL.common('images/video/problem-solving/sub-title_student-app@3x.png')} 3x`}
                                />
                                <img
                                  className="title"
                                  src={s3URL.common('images/video/problem-solving/title@1x.png')}
                                  srcSet={`${s3URL.common(
                                    'images/video/problem-solving/title@2x.png',
                                  )} 2x, ${s3URL.common('images/video/problem-solving/title@3x.png')} 3x`}
                                />
                              </div>
                            </S.Thumbnail>
                          ) : null
                        }
                        videoUrl={problemScoring.problem.video?.videoUrl as string}
                        onPlayCallback={onPlayCallback}
                        subtitleUrl={problemScoring.problem.video?.subtitleUrl ?? undefined}
                        defaultPlayingTime={!isIOS ? videoPlaybackTime : undefined}
                        onProgressCallback={(time) => {
                          setVideoPlaybackTime(time)
                        }}
                        onStartCallback={() => {
                          if (
                            commonRepo.studentId &&
                            commonRepo.academy &&
                            'video' in problemScoring.problem &&
                            problemScoring.problem.video?.id
                          ) {
                            mixpanelClient.track('문풀동재생_Click', {
                              요금제: commonRepo.academy.subscribePlan,
                              문풀동쌍둥이여부:
                                problemScoring.problem.videoMatched === undefined
                                  ? 'unknown'
                                  : problemScoring.problem.videoMatched === false,
                              문풀동문제난이도: problemScoring.problem.level,
                              videoId: problemScoring.problem?.video?.id,
                              problemId: problemScoring.problem.id,
                              conceptId: problemScoring.problem.conceptId,
                              workbookProblemId: problemScoring.workbookProblemId,
                              채점결과: problemScoring.채점결과,
                              학생앱문풀동컨텐츠타입:
                                viewOption.content.kind === 'WORKSHEET'
                                  ? '학습지'
                                  : viewOption.content.kind === 'WORKBOOK'
                                    ? '교재'
                                    : '챌린지',
                              학생앱문풀동컨텐츠세부타입: viewOption.content.type,
                            })
                          }
                        }}
                      />
                    ) : (
                      <div className="no-video">풀이동영상이 없어요</div>
                    )}
                  </>
                )}
              </div>
            )}

            {problemScoring.isSubmitted && problemScoring.문제이미지 && _isMobile && (
              <div className="problem-scoring-drawer-body-content-item">
                <div className="title-wrapper">
                  <div className="title">문제</div>
                  <button
                    className={clsx('icon-button', isShowProblemImage && 'opened')}
                    onClick={() => setIsShowProblemImage((prev) => !prev)}
                  >
                    <Icon name="icon_chevron_down" size={20} color={colors.gray.$900} />
                  </button>
                </div>
                {isShowProblemImage && (
                  <img src={problemScoring.문제이미지} alt="문제 이미지" width={344} />
                )}
              </div>
            )}

            {problemScoring.isSubmitted && isSubmittedAnswerAvailable && (
              <div className="problem-scoring-drawer-body-content-item">
                <div className="problem-scoring-drawer-body-content-item-header">
                  {/* 필기 restriction 차단 시 제목 "문제" 단독 (내풀이 단어/필기 토글/필기없음 메시지 모두 미노출) */}
                  <div className="title">
                    {viewOption.isHandwrittenRestricted
                      ? '문제'
                      : `${!_isMobile ? '문제 ・ ' : ''}내풀이`}
                  </div>
                  {!viewOption.isHandwrittenRestricted &&
                    (!_isMobile ? (
                      hasNoteData ? (
                        <button onClick={toggleHiddenNoteData} className="icon-button">
                          <Icon
                            name={isHiddenNoteData ? 'icon_eye_closed' : 'icon_eye_opened'}
                            size={22}
                            color={colors.gray.$800}
                          />
                        </button>
                      ) : (
                        <p className="no-writing">필기한 기록이 없어요</p>
                      )
                    ) : (
                      <></>
                    ))}
                </div>
                {viewOption.isHandwrittenRestricted ? (
                  // restriction 시 문제 이미지만 단독 노출 (필기 컴포넌트 X)
                  problemScoring.문제이미지 && (
                    <img src={problemScoring.문제이미지} alt="문제 이미지" width={344} />
                  )
                ) : hasNoteData && problemScoring.문제이미지 && !isHiddenNoteData ? (
                  <SimpleBar
                    key="with-note-data"
                    style={{
                      maxHeight: 정답해설_공개 || 문풀동공개 ? '312px' : '100%',
                    }}
                  >
                    <div className="note-container">
                      <ReadOnlyHandwrittenNote
                        noteData={noteData}
                        readonlyNoteData={teacherNoteData}
                        renderPriority="readonlyNoteData"
                        preventScale
                        preventOverflow
                        isSlideToNoteRect={_isMobile}
                      >
                        <img src={problemScoring.문제이미지} alt="문제 이미지" width={344} />
                      </ReadOnlyHandwrittenNote>
                      {_isMobile && (
                        <button type="button" className="note-zoom-in" onClick={openNoteViewer}>
                          <Icon name="icon_zoom_in" size={16} />
                        </button>
                      )}
                    </div>
                  </SimpleBar>
                ) : (
                  emptyCase
                )}
              </div>
            )}

            <div className="problem-scoring-drawer-body-content-item">
              <div className="title">해설</div>
              {정답해설_공개 ? (
                <img src={problemScoring.문제해설이미지} alt="문제 해설 이미지" width={344} />
              ) : (
                <div className="no-answer">
                  {viewOption.studentAppSetting?.채점후정답해설공개 && (
                    <>채점 후 정답・해설이 공개됩니다.</>
                  )}
                  {!viewOption.studentAppSetting?.채점후정답해설공개 && (
                    <>채점 후 정답・해설이 비공개 상태입니다.</>
                  )}
                </div>
              )}
            </div>
          </S.DrawerBodyContent>
        </Drawer.Body>
      </Drawer.Content>
    </Drawer>
  )
}
